// src/components/layout/Header.jsx
import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  MessageSquare,
  Menu,
  X,
  ChevronDown,
  User,
  LogOut,
  Settings,
  Award,
  BookOpen,
  Sparkles,
  Home,
  Target,
  Users,
  Feather
} from 'lucide-react';
import Button from '../shared/Button';
import { useAuth } from '../../context/AuthContext';
import { useApp } from '../../context/AppContext';
import { noteCompanionEntry } from '../../services/companionModuleContext';

const Header = ({ scrolled, mobileMenuOpen, setMobileMenuOpen }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logOut, redirectToAuth } = useAuth();
  const { assessmentResults } = useApp();

  const navigation = [
    { name: 'Assessment', href: '/assessment', icon: Target },
    { name: 'Dive In', href: '/modules', icon: BookOpen },
    { name: 'Toolkit', href: '/toolkit', icon: Sparkles },
    { name: 'Dashboard', href: '/dashboard', icon: Award },
    { name: 'Marlow', href: '/marlow', icon: Feather },
    { name: 'Resources', href: '/resources', icon: MessageSquare },
  ];

  const handleLogout = async () => {
    try {
      await logOut();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      scrolled ? 'bg-gunmetal/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
    }`}>
      <nav className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <img
              src="/art-of-story-logo.svg"
              alt="The Art of Story"
              className="w-16 h-16 group-hover:scale-110 transition-transform flex-shrink-0"
            />
            <div className="hidden sm:flex flex-col">
              <h1 className="text-xl font-bold text-white group-hover:text-bright-gold transition-colors" style={{ lineHeight: '1.2' }}>
                The Art of Story
              </h1>
              <p className="text-xs text-muted-gold group-hover:text-gold transition-colors" style={{ lineHeight: '1', marginTop: '2px' }}>A Dive Deep Dialogues App</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navigation.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.href;
              
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={item.href === '/marlow' ? noteCompanionEntry : undefined}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all ${
                    isActive
                      ? 'bg-gold/20 text-bright-gold'
                      : 'text-pearl hover:text-bright-gold hover:bg-gunmetal/20'
                  }`}
                >
                  <Icon className="w-4 h-4" aria-hidden="true" />
                  <span className="font-medium">{item.name}</span>
                </Link>
              );
            })}
          </div>

          {/* Desktop Actions */}
          <div className="hidden lg:flex items-center gap-4">
            {user ? (
              <>
                {/* User Progress Badge */}
                {assessmentResults && (
                  <div className="flex items-center gap-2 px-3 py-1 bg-green-500/20 rounded-full">
                    <Award className="w-4 h-4 text-green-400" aria-hidden="true" />
                    <span className="text-sm text-green-300">
                      {assessmentResults.archetype || 'Profile complete'}
                    </span>
                  </div>
                )}

                {/* Settings Icon */}
                <Link
                  to="/settings"
                  className="p-2 rounded-lg hover:bg-gunmetal/20 transition-colors"
                  aria-label="Settings"
                >
                  <Settings className="w-5 h-5 text-muted-gold hover:text-bright-gold transition-colors" aria-hidden="true" />
                </Link>

                {/* Logout Button */}
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-gunmetal/20 transition-colors"
                  aria-label="Log out"
                >
                  <LogOut className="w-5 h-5 text-muted-gold" aria-hidden="true" />
                  <span className="text-pearl hover:text-bright-gold transition-colors">Log Out</span>
                </button>
              </>
            ) : (
              <Button
                onClick={() => redirectToAuth('signin')}
                variant="secondary"
              >
                Log In
              </Button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 rounded-lg hover:bg-white/10 transition-colors"
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6 text-white" aria-hidden="true" />
            ) : (
              <Menu className="w-6 h-6 text-white" aria-hidden="true" />
            )}
          </button>
        </div>
      </nav>
    </header>
  );
};

export default Header;